import { useEffect, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

function today() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function CashBook() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [opening, setOpening] = useState(0);
  const [data, setData] = useState({ rows: [], total_dr: 0, total_cr: 0, closing_balance: 0, opening_balance: 0 });

  const load = () => api.get(`/reports/cash-book?from_date=${from}&to_date=${to}&opening=${opening}`)
    .then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, [from, to, opening]); // eslint-disable-line

  const pg = usePagination(data.rows);
  const [org, setOrg] = useState(null);
  useEffect(() => { api.get("/organisation").then(r => setOrg((r.data || [])[0] || null)).catch(() => {}); }, []);
  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/reports/cash-book/pdf?from_date=${from}&to_date=${to}&opening=${opening}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "cash_book.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="p-8 print:hidden">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Wallet className="w-3 h-3"/> Accounts · Cash inflow & outflow</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Cash Book</h1>
        </div>
        <div className="flex items-end gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Opening ₹</Label><Input data-testid="opening" type="number" value={opening} onChange={(e) => setOpening(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-28 text-right"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">From</Label><Input data-testid="from-date" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">To</Label><Input data-testid="to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border w-40"/></div>
          <Button data-testid="export-pdf-cashbook" size="sm" variant="outline" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
          <Button data-testid="print-cashbook" size="sm" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Opening</div><div className="font-display font-black text-xl mt-1">{inr(data.opening_balance)}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Dr (In)</div><div className="font-display font-black text-xl text-green-500 mt-1" data-testid="total-dr">{inr(data.total_dr)}</div></div>
        <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Cr (Out)</div><div className="font-display font-black text-xl text-destructive mt-1" data-testid="total-cr">{inr(data.total_cr)}</div></div>
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Closing</div><div className="font-display font-black text-xl text-primary mt-1" data-testid="closing">{inr(data.closing_balance)}</div></div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Ref #</th>
              <th className="text-left p-3">Particulars</th>
              <th className="text-right p-3">Debit (In)</th>
              <th className="text-right p-3">Credit (Out)</th>
              <th className="text-right p-3">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No cash transactions in this range</td></tr>}
            {pg.pageItems.map((r, i) => (
              <tr key={i} data-testid={`cash-row-${i}`} className="border-t border-border">
                <td className="p-3 font-mono text-xs">{r.date}</td>
                <td className="p-3 font-mono text-xs text-primary uppercase">{r.ref}</td>
                <td className="p-3 text-xs">{r.particulars}<div className="text-muted-foreground italic">{r.narration}</div></td>
                <td className="p-3 text-right font-mono text-xs text-green-500">{r.dr ? inr(r.dr) : "-"}</td>
                <td className="p-3 text-right font-mono text-xs text-destructive">{r.cr ? inr(r.cr) : "-"}</td>
                <td className="p-3 text-right font-mono">{inr(r.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      <div className="hidden print:block print:bg-white print:text-black" id="print-cashbook">
        <div className="p-4">
          <div className="text-xl font-bold uppercase">{org?.business_name || "SynqMoto ERP Workshop"}</div>
          <div className="text-[11px] uppercase">Establishment: {org?.establishment_name || org?.branch_name || org?.business_name || "—"}</div>
          <div className="text-lg font-bold mt-2">Cash Book</div>
          <div className="text-[11px] mb-2">Period: {from} to {to} · {data.rows.length} rows</div>
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr>
                <th className="border border-black p-1 text-left">Date</th>
                <th className="border border-black p-1 text-left">Ref #</th>
                <th className="border border-black p-1 text-left">Particulars</th>
                <th className="border border-black p-1 text-right">Debit (In)</th>
                <th className="border border-black p-1 text-right">Credit (Out)</th>
                <th className="border border-black p-1 text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={3}>Opening Balance</td>
                <td className="border border-black p-1 text-right">{inr(data.opening_balance)}</td>
                <td className="border border-black p-1 text-right">—</td>
                <td className="border border-black p-1 text-right">{inr(data.opening_balance)}</td>
              </tr>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td className="border border-black p-1">{r.date}</td>
                  <td className="border border-black p-1">{r.ref}</td>
                  <td className="border border-black p-1">{r.particulars}{r.narration ? ` — ${r.narration}` : ""}</td>
                  <td className="border border-black p-1 text-right">{r.dr ? inr(r.dr) : "—"}</td>
                  <td className="border border-black p-1 text-right">{r.cr ? inr(r.cr) : "—"}</td>
                  <td className="border border-black p-1 text-right">{inr(r.balance)}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={3}>TOTALS</td>
                <td className="border border-black p-1 text-right">{inr(data.total_dr)}</td>
                <td className="border border-black p-1 text-right">{inr(data.total_cr)}</td>
                <td className="border border-black p-1 text-right"></td>
              </tr>
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={3}>Closing Balance</td>
                <td className="border border-black p-1 text-right"></td>
                <td className="border border-black p-1 text-right"></td>
                <td className="border border-black p-1 text-right">{inr(data.closing_balance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
